import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
} from '@angular/core';
import { ApexOptions } from 'ng-apexcharts';
import moment from 'moment';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ResearchService } from 'app/modules/research/research.service';
import { TimeSeriesChartService } from '../time-series-charts.service';
import { TimeSeriesChart } from '../time-series-charts.types';
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FieldDef } from 'app/core/models';
import { getAllowedFieldsByString, removeArchivedFieldsFromTableDefinition } from 'app/shared/functions';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { SelectableFieldDef } from 'app/shared/components/column-selector/column-selector.types';
import { AddTimeSeriesValueComponent } from '../add-time-series-value/add-time-series-value.component';
import { groupBy } from 'lodash-es';
import { ActivatedRoute, Data } from '@angular/router';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import UrlUtils from '@shared/Utils/url.utils';
import cloneDeep from 'lodash-es/cloneDeep';
import { FormatNumberPipe } from '@shared/pipes/format-number.pipe';
import { CurrencyPipe } from '@angular/common';
import { Dictionary } from '@shared/models';
import { AuthService } from '@core/services/auth.service';
import { NewPermission, Permission } from '@core/enums/permission';

type TimeSeriesItem = {
  label: string;
  name: string;
  data: any[];
  table_data: any;
  table_data2: any[];
};

type SumAndAvgs = {
  display: 'sum' | 'avg';
  sum?: string;
  avg?: string;
  shouldUpdate: boolean;
};

@Component({
  selector: 'time-series-chart',
  templateUrl: './time-series-chart.component.html',
  styleUrls: ['./time-series-chart.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeSeriesChartComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  @Input() chart: TimeSeriesChart;
  @Input() timeSeriesFields: FieldDef[] = [];
  @Input() fieldDefs: FieldDef[] = [];
  @Input() hide = false;
  @Input() editPermission: Permission | NewPermission;
  @Input() panelLayout: boolean = false;
  @Input() panelOptions: {
    startDate: string;
    endDate: string;
    activeTab: string;
  };

  NewPermission = NewPermission;

  timeSeriesChartDateForm: UntypedFormGroup;
  fields: SelectableFieldDef[];
  displayColumns: string[] = [];
  chartOptions: ApexOptions;
  cadence: string = 'month';
  chartData: any = {};
  data: any;
  dataSource: MatTableDataSource<TimeSeriesItem>;
  displayedColumns: string[];
  clickedIndexTab: number;
  sumAndAvgs: Dictionary<SumAndAvgs> = {};

  /**
   * Constructor
   */
  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private _changeDetectorRef: ChangeDetectorRef,
    private _researchService: ResearchService,
    private _timeSeriesChartService: TimeSeriesChartService,
    private _matDialog: MatDialog,
    private authService: AuthService,
    private _formBuilder: UntypedFormBuilder,
  ) {
    super();
  }

  toggleSumOrAvg(name: string, aggregator: 'sum' | 'avg'): void {
    if (this.sumAndAvgs[name]) {
      this.sumAndAvgs[name].display = aggregator;
    }
    this.onSaveAggregators();
  }

  getSumOrAvg(element: TimeSeriesItem): string {
    const fieldDef = this.fields.find((field) => field.name === element.name);

    let fieldSumAndAvgs: SumAndAvgs;
    if (fieldDef) {
      fieldSumAndAvgs = this.sumAndAvgs[element.name];
    }

    const sum = element.data.reduce((acc: number, cur: any) => (Number(cur) || 0) + acc, 0);

    this.sumAndAvgs[element.name] = {
      display: fieldSumAndAvgs?.display || 'sum',
      sum: sum !== 0 ? this._formatNumericValue(fieldDef, sum) : null,
      avg: sum !== 0 ? this._formatNumericValue(fieldDef, String(sum / element.data.length)) : null,
      shouldUpdate: false,
    };

    const response = this._formatSumAvgValue(this.sumAndAvgs[element.name]);
    return response;
  }

  getAggregationValue(element: TimeSeriesItem) {
    return this.sumAndAvgs[element.name][this.sumAndAvgs[element.name].display];
  }

  private _formatNumericValue(fieldDef: FieldDef, value: string): string {
    switch (fieldDef.type) {
      case 'number':
        return new FormatNumberPipe().transform(value, fieldDef.meta?.scale || 0);
      case 'decimal':
        return new FormatNumberPipe().transform(value, fieldDef.meta?.scale || 2);
      case 'money':
        // eslint-disable-next-line no-case-declarations
        const precision = fieldDef.meta?.scale || 0;
        if (value) {
          return new CurrencyPipe('en-US').transform(value, 'USD', 'symbol', '1.' + precision + '-' + precision);
        } else {
          return null;
        }
      default:
        return null;
    }
  }

  private _formatSumAvgValue({ display, sum, avg }: SumAndAvgs): string {
    return `${display}: ${display === 'sum' ? sum : avg}`;
  }

  get activeTab(): number {
    return (
      this.panelOptions?.activeTab || UrlUtils.getFromLocalStorage('analytics-ts-last-active-tab-' + this.chart.id) || 0
    );
  }

  ngOnInit() {
    this.timeSeriesChartDateForm = this._formBuilder.group({
      start_date: [this.panelLayout ? this.panelOptions.startDate : ''],
      end_date: [this.panelLayout ? this.panelOptions.endDate : ''],
    });

    this._timeSeriesChartService.timeSeriesFields$
      .pipe(untilComponentDestroyed(this))
      .subscribe((fields: FieldDef[]) => {
        this.timeSeriesFields = fields;
        this.getChart();
      });

    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.data = data;
    });
  }

  getChart(): void {
    if (!this.chart) return;

    this._timeSeriesChartService
      .getTimeSeriesChart(this.chart.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe((timeSeriesChart) => {
        this.chart = timeSeriesChart;
        if (this.chart.definition.fields) {
          this.displayColumns = removeArchivedFieldsFromTableDefinition(
            this.chart.definition?.fields,
            this.timeSeriesFields,
          );
        }

        if (Object.keys(this.chart.definition.aggregators || {}).length > 0) {
          this.sumAndAvgs = this.chart.definition.aggregators;
        }

        if (this.data?.fieldDefs) {
          this.fieldDefs = getAllowedFieldsByString(this.data.fieldDefs, this.chart.related_to_type);
        }

        if (this.fieldDefs.length > 0) {
          this.fieldDefs = this.fieldDefs.filter(
            (field) => field.meta?.is_custom && (field.type?.includes('money') || field.type?.includes('number')),
          );
        }

        if (this.chart.definition.cadence) {
          this.cadence = this.chart.definition.cadence;
        }

        if (this.chart.definition.filters && !this.panelLayout) {
          this.timeSeriesChartDateForm.get('start_date').setValue(moment(this.chart.definition.filters.start_date));
          this.timeSeriesChartDateForm.get('end_date').setValue(moment(this.chart.definition.filters.end_date));
        } else {
          const startDate = this.panelLayout ? moment(this.panelOptions.startDate) : moment().subtract(365, 'days');
          const endDate = this.panelLayout ? moment(this.panelOptions.endDate) : moment();

          this.timeSeriesChartDateForm.get('start_date').setValue(startDate);
          this.timeSeriesChartDateForm.get('end_date').setValue(endDate);
        }

        this.fields = TimeSeriesChartComponent.getFieldsOrderedByDisplayColumns(
          this.timeSeriesFields,
          this.displayColumns,
        );

        this.getChartData();
      });
  }

  private static getFieldsOrderedByDisplayColumns(fields: FieldDef[], displayColumns: string[]): SelectableFieldDef[] {
    const fieldDefsInColumns = [];
    const fieldDefsNotInColumns = [];

    fields.forEach((field) => {
      const indexOfFieldAsColumn = displayColumns.indexOf(field.name);

      if (indexOfFieldAsColumn !== -1) {
        fieldDefsInColumns[indexOfFieldAsColumn] = { ...field, selected: true };
      } else {
        fieldDefsNotInColumns.push({ ...field, selected: false });
      }
    });

    return [...fieldDefsInColumns, ...fieldDefsNotInColumns];
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    super.ngOnDestroy();
  }

  getChartData(): void {
    let startDate = moment().subtract(365, 'days').format('YYYY-MM-DD');
    let endDate = moment().subtract(365, 'days').format('YYYY-MM-DD');

    if (this.panelLayout) {
      startDate = moment(this.panelOptions.startDate).format('YYYY-MM-DD');
      endDate = moment(this.panelOptions.endDate).format('YYYY-MM-DD');
    } else {
      startDate = this.timeSeriesChartDateForm.get('start_date').value?.format('YYYY-MM-DD');
      endDate = this.timeSeriesChartDateForm.get('end_date').value?.format('YYYY-MM-DD');
    }

    this._timeSeriesChartService
      .getTimeSeriesChartData(this.chart.id, startDate, endDate)
      .pipe(untilComponentDestroyed(this))
      .subscribe((chartData) => {
        this.chartData = chartData;
        this.renderChartData();
      });
  }

  groupData(data, fieldName = '') {
    let groupedData = data;
    let aggregationDisplay = this.sumAndAvgs[fieldName]?.display || 'sum';

    if (this.cadence == 'month') {
      let groupedVals = groupBy(data, (item) => moment(item[0]).format('MMM YYYY'));
      groupedData = [];
      for (const key in groupedVals) {
        let sum = groupedVals[key].reduce((a, b) => a + b[1], 0);
        let vals = [];
        if (aggregationDisplay == 'avg') {
          sum = sum / groupedVals[key].length;
        }
        vals.push(key);
        vals.push(sum);
        groupedData.push(vals);
      }
    } else if (this.cadence == 'quarter') {
      let groupedVals = groupBy(data, (item) => moment(item[0]).format('[Q]Q-YYYY'));
      groupedData = [];
      for (const key in groupedVals) {
        let sum = groupedVals[key].reduce((a, b) => a + b[1], 0);
        let vals = [];
        vals.push(key);
        if (aggregationDisplay == 'avg') {
          sum = sum / groupedVals[key].length;
        }
        vals.push(sum);
        groupedData.push(vals);
      }
    } else if (this.cadence == 'year') {
      let groupedVals = groupBy(data, (item) => moment(item[0]).format('YYYY'));
      groupedData = [];
      for (const key in groupedVals) {
        let sum = groupedVals[key].reduce((a, b) => a + b[1], 0);
        let vals = [];
        vals.push(key);
        if (aggregationDisplay == 'avg') {
          sum = sum / groupedVals[key].length;
        }
        vals.push(sum);
        groupedData.push(vals);
      }
    }
    return groupedData;
  }

  renderChartData(): void {
    let series: TimeSeriesItem[] = [];
    let dates = [];

    for (const fieldName in this.chartData) {
      let row = this.chartData[fieldName];
      let label = row.label;
      let sortedData = row.data.sort((a, b) => moment(a[0]).unix() - moment(b[0]).unix());
      sortedData = this.groupData(sortedData, fieldName);

      let groupedData = {};
      for (const data of sortedData) {
        const key = data[0];
        const model = {};
        model[fieldName] = data[1];
        groupedData[key] = { val: data[1], model: model, date: data[0], id: data[2] };
      }

      series.push({
        label: label,
        name: fieldName,
        data: sortedData.map((item) => item[1]),
        table_data: groupedData,
        table_data2: sortedData.map((item) => {
          const model = {};
          model[fieldName] = item[1];
          return { val: item[1], model: model, date: item[0] };
        }),
      });
      for (const item of sortedData) {
        if (!dates.includes(item[0])) {
          dates.push(item[0]);
        }
      }
      //dates = sortedData.map((item) => item[0]);
    }
    dates = dates.sort((a, b) => moment(a).unix() - moment(b).unix());
    //dates = dates.map((item) => moment(item).format('MMM YYYY'));

    this.chartOptions = {
      chart: {
        height: 250,
        type: 'line',
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'straight',
      },
      grid: {
        row: {
          colors: ['#f3f3f3', 'transparent'], // takes an array which will be repeated on columns
          opacity: 0.5,
        },
      },
      series: series,
      tooltip: {
        theme: 'dark',
        x: {
          format: 'MMM yyyy',
        },
        y: {
          formatter: (value) => {
            return value?.toString();
          },
        },
      },
      xaxis: {
        categories: dates,
      },
    };

    this.displayedColumns = ['label', ...dates];

    for (const element of series) {
      this.getSumOrAvg(element);
    }

    this.dataSource = new MatTableDataSource(series);
    this._changeDetectorRef.markForCheck();
  }

  getAggregator(element: TimeSeriesItem): string {
    return this.sumAndAvgs[element.name]?.display;
  }

  exportAsCSV(): void {
    this._timeSeriesChartService.exportTimeSeriesChart(this.chart.id).pipe(untilComponentDestroyed(this)).subscribe();
  }

  deleteTimeSeriesChart(): void {
    this._timeSeriesChartService.deleteTimeSeriesChart(this.chart.id).pipe(untilComponentDestroyed(this)).subscribe();
  }

  onSaveColumns(selectableFieldDefs: SelectableFieldDef[]): void {
    let columns = selectableFieldDefs.map((selectableFieldDef) => {
      const { name, label, meta, type } = selectableFieldDef;
      return {
        label,
        name: name,
        isCustom: meta?.is_custom ?? false,
        fieldDef: selectableFieldDef,
        type,
      };
    });
    this.displayColumns = columns.map(({ name, fieldDef: { meta } }) => name);

    this.chart.definition.fields = this.displayColumns;
    this._timeSeriesChartService.saveTimeSeriesChart(this.chart).pipe(untilComponentDestroyed(this)).subscribe();
  }

  onSaveAggregators(): void {
    this.chart.definition.aggregators = this.sumAndAvgs;
    const newChart = cloneDeep(this.chart);
    this._timeSeriesChartService
      .saveTimeSeriesChart(newChart, true)
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        this.renderChartData();
      });
  }

  onDateChange(event: MatDatepickerInputEvent<Date>) {
    this.getChartData();
    this.chart.definition.filters = {
      start_date: this.timeSeriesChartDateForm.get('start_date').value?.format('YYYY-MM-DD'),
      end_date: this.timeSeriesChartDateForm.get('end_date').value?.format('YYYY-MM-DD'),
    };
    this._timeSeriesChartService.saveTimeSeriesChart(this.chart, true).pipe(untilComponentDestroyed(this)).subscribe();
  }

  addTimeSeriesValue() {
    const dialogRef = this._matDialog.open(AddTimeSeriesValueComponent, {
      panelClass: 'mailbox-compose-dialog',
      autoFocus: false,
      data: {
        fields: this.fieldDefs || this.fields,
        timeSeriesChartId: this.chart.id,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.getChart();
      this._changeDetectorRef.markForCheck();
    });
  }

  lookupField(name) {
    let fields = this.fields.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }

  onUpdateTimeSeriesValue($event, data, fieldDef) {
    const val = $event[fieldDef.name];

    this._timeSeriesChartService
      .setFieldValue(this.chart.id, val, data.date, fieldDef.name)
      .subscribe((timeSeriesChart) => {
        this.getChartData();
      });
  }

  onTabChanged($event): void {
    this.clickedIndexTab = $event.index;
    UrlUtils.saveToLocalStorage('analytics-ts-last-active-tab-' + this.chart.id, this.clickedIndexTab);
    if (this.clickedIndexTab === 0) {
      window.dispatchEvent(new Event('resize')); // hack to force chart rerender
    }
  }

  onCandenceChanged($event) {
    this.renderChartData();
    this.chart.definition.cadence = this.cadence;
    this._timeSeriesChartService.saveTimeSeriesChart(this.chart).pipe(untilComponentDestroyed(this)).subscribe();
  }

  getDataFromRow(date, row) {
    if (date in row.table_data) {
      return row.table_data[date];
    }
  }

  onClickField($event, data, fieldDef) {
    const dialogRef = this._matDialog.open(AddTimeSeriesValueComponent, {
      panelClass: 'mailbox-compose-dialog',
      autoFocus: false,
      data: {
        fields: this.fieldDefs || this.fields,
        timeSeriesChartId: this.chart.id,
        model: {
          val: data.val,
          date: data.date,
          field_name: fieldDef.name,
          related_to_type: this.chart.related_to_type,
          field_label: fieldDef.label,
          id: data.id,
        },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.getChart();
      this._changeDetectorRef.markForCheck();
    });
  }

  hasEditPermission(): [Permission | NewPermission, boolean] | boolean {
    if (this.editPermission) {
      return [this.editPermission, this.data.project?.permissions?.is_editable];
    }

    return true;
  }
}
